import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Ban, CalendarClock, Save } from "lucide-react";

const DAYS = [
  { key: "monday", label: "Maandag" },
  { key: "tuesday", label: "Dinsdag" },
  { key: "wednesday", label: "Woensdag" },
  { key: "thursday", label: "Donderdag" },
  { key: "friday", label: "Vrijdag" },
  { key: "saturday", label: "Zaterdag" },
  { key: "sunday", label: "Zondag" },
];
const EMPTY_DAY = { enabled: false, periods: [], breaks: [] };

export default function Availability() {
  const [weekly, setWeekly] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/availability").then(({ data }) => {
    const w = data.weekly || {};
    const norm = {};
    DAYS.forEach((d) => { norm[d.key] = w[d.key] || { ...EMPTY_DAY }; });
    setWeekly(norm);
    setOverrides(data.overrides || []);
  }).catch(() => {});

  useEffect(() => { load(); }, []);

  const setDay = (key, patch) => setWeekly((w) => ({ ...w, [key]: { ...w[key], ...patch } }));

  const addPeriod = (key, field) => setDay(key, { [field]: [...(weekly[key][field] || []), { start: "09:00", end: "17:00" }] });
  const updPeriod = (key, field, idx, p) => {
    const arr = [...weekly[key][field]]; arr[idx] = { ...arr[idx], ...p }; setDay(key, { [field]: arr });
  };
  const rmPeriod = (key, field, idx) => setDay(key, { [field]: weekly[key][field].filter((_, i) => i !== idx) });

  const saveWeekly = async () => {
    setSaving(true);
    try {
      await api.put("/admin/availability/weekly", { config: weekly });
      toast.success("Beschikbaarheid opgeslagen");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!weekly) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-brand-pink" size={28} /></div>;

  return (
    <div data-testid="admin-availability">
      <h1 className="font-display text-4xl font-semibold text-brand-charcoal">Beschikbaarheid</h1>
      <p className="text-brand-muted mt-1">Stel per dag je openingsuren en pauzes in. Klanten zien enkel beschikbare momenten.</p>

      <div className="space-y-3 mt-8">
        {DAYS.map((d) => {
          const day = weekly[d.key];
          return (
            <div key={d.key} className="bg-white rounded-2xl border border-brand-border p-4 sm:p-5" data-testid={`day-${d.key}`}>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={day.enabled} onChange={(e) => setDay(d.key, { enabled: e.target.checked })} className="h-5 w-5 accent-brand-pinkhover" data-testid={`day-toggle-${d.key}`} />
                  <span className="font-display text-xl font-medium text-brand-charcoal">{d.label}</span>
                </label>
                {!day.enabled && <span className="text-sm text-brand-muted">Gesloten</span>}
              </div>

              {day.enabled && (
                <div className="mt-4 space-y-4 pl-8">
                  <PeriodGroup title="Beschikbaar" items={day.periods} onAdd={() => addPeriod(d.key, "periods")} onUpd={(i, p) => updPeriod(d.key, "periods", i, p)} onRm={(i) => rmPeriod(d.key, "periods", i)} testid={`periods-${d.key}`} />
                  <PeriodGroup title="Pauze" items={day.breaks} onAdd={() => addPeriod(d.key, "breaks")} onUpd={(i, p) => updPeriod(d.key, "breaks", i, p)} onRm={(i) => rmPeriod(d.key, "breaks", i)} muted testid={`breaks-${d.key}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button onClick={saveWeekly} disabled={saving} className="mt-6 flex items-center gap-2 bg-brand-charcoal text-white font-semibold px-7 py-3.5 rounded-full disabled:opacity-60" data-testid="save-availability-button">
        {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Beschikbaarheid opslaan
      </button>

      <Overrides overrides={overrides} reload={load} />
    </div>
  );
}

function PeriodGroup({ title, items, onAdd, onUpd, onRm, muted, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-sm font-medium ${muted ? "text-brand-muted" : "text-brand-charcoal"}`}>{title}</span>
        <button onClick={onAdd} className="flex items-center gap-1 text-xs text-brand-pinkhover font-medium"><Plus size={14} /> Toevoegen</button>
      </div>
      {items.length === 0 && <p className="text-xs text-brand-muted">Geen {title.toLowerCase()} ingesteld.</p>}
      <div className="space-y-2">
        {items.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <input type="time" value={p.start} onChange={(e) => onUpd(i, { start: e.target.value })} className="rounded-lg border border-brand-border px-2.5 py-1.5 text-sm" />
            <span className="text-brand-muted">–</span>
            <input type="time" value={p.end} onChange={(e) => onUpd(i, { end: e.target.value })} className="rounded-lg border border-brand-border px-2.5 py-1.5 text-sm" />
            <button onClick={() => onRm(i)} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Overrides({ overrides, reload }) {
  const [date, setDate] = useState("");
  const [mode, setMode] = useState("block"); // block | custom
  const [periods, setPeriods] = useState([{ start: "16:00", end: "20:00" }]);
  const [label, setLabel] = useState("");

  const save = async () => {
    if (!date) return toast.error("Kies een datum");
    try {
      await api.post("/admin/availability/override", {
        date, blocked: mode === "block",
        periods: mode === "custom" ? periods : [], breaks: [], label,
      });
      toast.success("Uitzondering opgeslagen");
      setDate(""); setLabel("");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (d) => {
    await api.delete(`/admin/availability/override/${d}`);
    toast.success("Verwijderd");
    reload();
  };

  return (
    <div className="mt-12" data-testid="overrides-section">
      <h2 className="font-display text-2xl font-medium text-brand-charcoal mb-1">Uitzonderingen & gesloten dagen</h2>
      <p className="text-brand-muted text-sm mb-5">Blokkeer een specifieke datum (feestdag, vrije dag) of geef aangepaste uren. Dit overschrijft het weekschema.</p>

      <div className="bg-white rounded-2xl border border-brand-border p-5 space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1.5">Datum</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full rounded-xl border border-brand-border px-3 py-2.5 text-sm" data-testid="override-date" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Type</label>
            <div className="flex bg-brand-neutral rounded-full p-1">
              <button onClick={() => setMode("block")} className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-medium ${mode === "block" ? "bg-brand-charcoal text-white" : "text-brand-muted"}`} data-testid="override-mode-block"><Ban size={14} /> Blokkeren</button>
              <button onClick={() => setMode("custom")} className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-medium ${mode === "custom" ? "bg-brand-charcoal text-white" : "text-brand-muted"}`} data-testid="override-mode-custom"><CalendarClock size={14} /> Aangepaste uren</button>
            </div>
          </div>
        </div>
        {mode === "custom" && (
          <div className="space-y-2">
            {periods.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="time" value={p.start} onChange={(e) => { const a = [...periods]; a[i].start = e.target.value; setPeriods(a); }} className="rounded-lg border border-brand-border px-2.5 py-1.5 text-sm" />
                <span>–</span>
                <input type="time" value={p.end} onChange={(e) => { const a = [...periods]; a[i].end = e.target.value; setPeriods(a); }} className="rounded-lg border border-brand-border px-2.5 py-1.5 text-sm" />
                {periods.length > 1 && <button onClick={() => setPeriods(periods.filter((_, x) => x !== i))} className="text-red-400"><Trash2 size={15} /></button>}
              </div>
            ))}
            <button onClick={() => setPeriods([...periods, { start: "09:00", end: "17:00" }])} className="text-xs text-brand-pinkhover font-medium flex items-center gap-1"><Plus size={14} /> Periode toevoegen</button>
          </div>
        )}
        <input placeholder="Label (optioneel, bv. Feestdag)" value={label} onChange={(e) => setLabel(e.target.value)} className="w-full rounded-xl border border-brand-border px-3 py-2.5 text-sm" data-testid="override-label" />
        <button onClick={save} className="bg-brand-charcoal text-white font-semibold px-6 py-3 rounded-full text-sm" data-testid="override-save">Uitzondering opslaan</button>
      </div>

      {overrides.length > 0 && (
        <div className="mt-4 space-y-2">
          {overrides.map((o) => (
            <div key={o.date} className="flex items-center justify-between bg-white rounded-xl border border-brand-border px-4 py-3" data-testid={`override-item-${o.date}`}>
              <div>
                <p className="font-medium text-brand-charcoal">{o.date} {o.label ? `· ${o.label}` : ""}</p>
                <p className="text-sm text-brand-muted">{o.blocked ? "Gesloten / geblokkeerd" : o.periods.map((p) => `${p.start}–${p.end}`).join(", ")}</p>
              </div>
              <button onClick={() => remove(o.date)} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
